import { stylesTemplate } from "./utilities/styles.js";
var __create = Object.create;
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : /* @__PURE__ */ Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
var __decoratorStart = (base) => [, , , __create(base?.[__knownSymbol("metadata")] ?? null)];
var __decoratorStrings = ["class", "method", "getter", "setter", "accessor", "field", "value", "get", "set"];
var __expectFn = (fn) => fn !== void 0 && typeof fn !== "function" ? __typeError("Function expected") : fn;
var __decoratorContext = (kind, name, done, metadata, fns) => ({ kind: __decoratorStrings[kind], name, metadata, addInitializer: (fn) => done._ ? __typeError("Already initialized") : fns.push(__expectFn(fn || null)) });
var __decoratorMetadata = (array, target) => __defNormalProp(target, __knownSymbol("metadata"), array[3]);
var __runInitializers = (array, flags, self, value) => {
  for (var i = 0, fns = array[flags >> 1], n = fns && fns.length; i < n; i++) fns[i].call(self);
  return value;
};
var __decorateElement = (array, flags, name, decorators, target, extra) => {
  var it, done, ctx, k = flags & 7, p = false;
  var j = 0;
  var extraInitializers = array[j] || (array[j] = []);
  var desc = k && (target = target.prototype, k < 5 && (k > 3 || !p) && __getOwnPropDesc(target, name));
  __name(target, name);
  for (var i = decorators.length - 1; i >= 0; i--) {
    ctx = __decoratorContext(k, name, done = {}, array[3], extraInitializers);
    it = (0, decorators[i])(target, ctx), done._ = 1;
    __expectFn(it) && (target = it);
  }
  return __decoratorMetadata(array, target), desc && __defProp(target, name, desc), p ? k ^ 4 ? extra : desc : target;
};
const ComponentConfig = (config) => {
  return function(constructor) {
    constructor.componentConfig = config;
    return constructor;
  };
};
class BaseComponent extends HTMLElement {
  constructor() {
    super();
    this.elementRef = null;
    const constructor = this.constructor;
    this.config = constructor.componentConfig;
    if (!this.config) {
      throw new Error(
        "Component config not found. Did you forget to use the @Component decorator?"
      );
    }
    this.shadow = this.attachShadow({ mode: "open" });
    this.validateConfig();
  }
  static get observedAttributes() {
    return this.componentConfig?.attributes?.map((attr) => attr.name) || [];
  }
  connectedCallback() {
    try {
      this.initializeComponent();
    } catch (error) {
      this.handleError("Failed to initialize component", error);
    }
  }
  disconnectedCallback() {
    this.cleanup();
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    try {
      this.handleAttributeChange(name, oldValue, newValue);
    } catch (error) {
      this.handleError(`Failed to handle attribute change: ${name}`, error);
    }
  }
  initializeComponent() {
    try {
      this.validateSlots();
      const componentRef = this.config.createComponent(this);
      if (!componentRef || !componentRef.element) {
        throw new Error(
          "Component creation failed: Invalid component reference"
        );
      }
      this.elementRef = componentRef;
      this.setupShadowDom(componentRef);
      this.afterInit();
    } catch (error) {
      this.handleError("Failed to initialize component", error);
    }
  }
  setupShadowDom(component) {
    this.shadow.appendChild(
      stylesTemplate({ styles: component.styles || "" }).content.cloneNode(true)
    );
    this.shadow.appendChild(component.element);
  }
  validateConfig() {
    const requiredFields = [
      "tagName",
      "createComponent"
    ];
    requiredFields.forEach((field) => {
      if (!this.config[field]) {
        throw new Error(`Missing required config field: ${field}`);
      }
    });
    if (this.config.attributes) {
      this.config.attributes.forEach((attr) => {
        if (!attr.name || !attr.handler) {
          throw new Error(
            "Invalid attribute config: requires name and handler"
          );
        }
      });
    }
  }
  async afterInit() {
    if (!this.elementRef) {
      this.handleError("Cannot initialize - missing element reference", null);
      return;
    }
    await this.executeLifecycleCallbacks();
    this.dispatchComponentEvent("umdComponent:ready", {
      shadowRoot: this.shadow,
      ref: this.elementRef
    });
  }
  handleAttributeChange(name, oldValue, newValue) {
    if (!this.elementRef) return;
    const handler = this.config.attributes?.find(
      (attr) => attr.name === name
    )?.handler;
    if (handler) {
      handler(this.elementRef, oldValue, newValue);
    }
  }
  cleanup() {
    this.elementRef = null;
  }
  /**
   * Helper method to dispatch component events with consistent structure
   */
  dispatchComponentEvent(eventName, detail) {
    const fullDetail = {
      ...detail,
      tagName: this.config.tagName,
      element: this,
      timestamp: Date.now()
    };
    this.dispatchEvent(
      new CustomEvent(eventName, {
        detail: fullDetail,
        bubbles: true,
        composed: true
      })
    );
  }
  handleError(message, error) {
    console.error(`[${this.config.tagName}]`, message, error);
    const errorType = this.elementRef ? "runtime" : message.toLowerCase().includes("validation") ? "validation" : "initialization";
    this.dispatchComponentEvent("umdComponent:error", {
      type: errorType,
      message,
      details: error
    });
  }
  validateSlots() {
    if (!this.config.slots) return;
    const errors = [];
    Object.entries(this.config.slots).forEach(([name, config]) => {
      const type = name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`).replace(/^-/, "");
      if (config.required) {
        const slotElement = this.querySelector(`[slot="${type}"]`);
        if (!slotElement) {
          errors.push({
            slot: name,
            error: "missing",
            message: `Required slot "${type}" is missing`
          });
        }
      }
      if (config.deprecated) {
        const slotElement = this.querySelector(`[slot="${type}"]`);
        if (slotElement) {
          errors.push({
            slot: name,
            error: "deprecated",
            message: `Slot "${type}" is deprecated. ${config.deprecated}`
          });
        }
      }
      if (config.allowedElements) {
        const slotElements = Array.from(
          this.querySelectorAll(`[slot="${type}"]`)
        );
        const invalidElements = slotElements.filter(
          (element) => !config.allowedElements.includes(element.tagName.toLowerCase())
        );
        if (invalidElements.length > 0) {
          errors.push({
            slot: name,
            error: "invalid-elements",
            message: `Slot "${type}" contains invalid elements. Allowed: ${config.allowedElements.join(
              ", "
            )}`,
            invalidElements
          });
        }
      }
    });
    if (errors.length > 0) {
      this.handleError("Slot validation failed", errors);
      errors.forEach((error) => {
        console.warn(`[${this.config.tagName}]`, error.message, error);
      });
    }
  }
  async executeLifecycleCallbacks() {
    const lifecycleMethods = [
      "beforeConnect",
      "afterConnect",
      "onReady"
    ];
    for (const method of lifecycleMethods) {
      await this.executeCallback(method);
    }
  }
  async executeCallback(name) {
    const callback = this.config[name];
    if (!callback) {
      return;
    }
    try {
      await Promise.resolve(callback(this.elementRef, this.shadow));
    } catch (error) {
      this.handleError(`Failed to execute ${name} callback`, error);
    }
  }
  getRef() {
    return this.elementRef;
  }
}
const createCustomElement = (config) => {
  var _Component_decorators, _init, _a;
  _Component_decorators = [ComponentConfig(config)];
  class Component extends (_a = BaseComponent) {
    constructor() {
      super();
    }
  }
  _init = __decoratorStart(_a);
  Component = __decorateElement(_init, 0, "Component", _Component_decorators, Component);
  __runInitializers(_init, 1, Component);
  return Component;
};
export {
  BaseComponent,
  createCustomElement
};
//# sourceMappingURL=model.js.map
